/*!
 * dslWrite.js — writing a value back into cue DSL
 * © 2025 Rob Canning — GPLv3
 *
 * The editors that write DSL (the inspector, button(), the connections view
 * through it) decide here whether a value goes in bare or quoted. The test
 * is the lexer's own: bare only when the value reads back as exactly one
 * token of a kind a value can be. A regex of "unsafe" characters let
 * trig:"adc.onset>0.5" out bare, and the cue no longer parsed.
 */

import { CueLexer } from './parser.js';

// Tokens a bare value can be. A cue-name keyword (audio, x, …) is not one:
// the grammar reads it as a keyword, so a value spelled like it is quoted.
const BARE_VALUE_TOKENS = new Set(['Identifier', 'NumberLiteral', 'NoteLiteral', 'RangeLiteral',
    'StringLiteral', 'HexColor', 'OscAddr', 'True', 'False', 'PatternName']);

/** A word the lexer reads as a cue-name keyword (audio, x, …): no value to the grammar. */
export function lexesAsKeyword(v) {
    if (!/^[A-Za-z_]\w*$/.test(v)) return false;
    const toks = CueLexer.tokenize(v).tokens;
    return toks.length === 1 && !BARE_VALUE_TOKENS.has(toks[0].tokenType.name);
}

/** Does `text` read back as one bare value, all of it? */
export function lexesAsOneValue(text) {
    const { tokens, errors } = CueLexer.tokenize(text);
    if (errors.length || tokens.length !== 1 || tokens[0].image !== text) return false;
    const kind = tokens[0].tokenType.name;
    // a path (audio/rain.flac) lexes as an OSC address too; only a leading
    // slash makes one, so a path stays quoted, as the editor always wrote it
    if (kind === 'OscAddr') return text.startsWith('/');
    return BARE_VALUE_TOKENS.has(kind);
}

/**
 * A string value as DSL: bare when it lexes as one value, else quoted. The
 * lexer has no escapes, so a value holding a double quote goes in single ones.
 */
export function dslValue(v) {
    const s = String(v);
    return lexesAsOneValue(s) ? s : dslString(s);
}

/** A string quoted, in single quotes when it holds a double one. */
export function dslString(v) {
    const s = String(v);
    return s.includes('"') && !s.includes("'") ? `'${s}'` : `"${s}"`;
}

/** Split an argument list at depth-0 commas, ignoring those inside (), [], {} and quotes. */
export function splitArgs(str) {
    const out = [];
    let depth = 0, start = 0, quote = null;
    for (let i = 0; i < str.length; i++) {
        const ch = str[i];
        if (quote) { if (ch === quote) quote = null; continue; }
        if (ch === '"' || ch === "'") quote = ch;
        else if (ch === '(' || ch === '[' || ch === '{') depth++;
        else if (ch === ')' || ch === ']' || ch === '}') depth--;
        else if (ch === ',' && depth === 0) { out.push(str.slice(start, i)); start = i + 1; }
    }
    out.push(str.slice(start));
    return out.map(s => s.trim()).filter(Boolean);
}
